import InputUpload from '@app/components/atoms/Images/InputUpload'
import LabelField from '@app/components/atoms/LabelField/LabelField'
import Section from '@app/components/atoms/Section/Section'
import { LOCALISE } from '@app/constant/DateTimeConstant'
import { START_WITH_SPACE } from '@app/constant/RegexConstant'
import { toTextFieldDateFormat } from '@app/libs/format'
import ClassicEditor from '@ckeditor/ckeditor5-build-classic'
import { CKEditor } from '@ckeditor/ckeditor5-react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Inertia } from '@inertiajs/inertia'
import { InertiaLink } from '@inertiajs/inertia-react'
import Autocomplete from '@mui/material/Autocomplete'
import Button from '@mui/material/Button'
import FormHelperText from '@mui/material/FormHelperText'
import Grid from '@mui/material/Grid'
import MenuItem from '@mui/material/MenuItem'
import { styled } from '@mui/material/styles'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import CampaignEditSchema from './CampaignEditSchema'

const GridCustomizer = styled(Grid)`
  margin-bottom: 25;
`

const TypographyCustomizer = styled(Typography)`
  color: #d32f2f;
  padding-left: 14px;
`

const DivCustomizer = styled('div')`
  .ck .ck-editor__main > .ck-editor__editable {
    min-height: 150px;
    border-bottom-left-radius: 5px;
    border-bottom-right-radius: 5px;
  }

  .ck-rounded-corners .ck.ck-editor__top .ck-sticky-panel .ck-toolbar {
    border-top-left-radius: 5px;
    border-top-right-radius: 5px;
  },
`

const CampaignEdit = ({ campaign, status, partners }) => {
  const { t } = useTranslation()

  const initialValues = {
    ...campaign,
    start_at: toTextFieldDateFormat(campaign.start_at),
    end_at: toTextFieldDateFormat(campaign.end_at),
    visible_from: toTextFieldDateFormat(campaign.visible_from),
    visible_to: toTextFieldDateFormat(campaign.visible_to),
  }

  const {
    register,
    trigger,
    handleSubmit,
    control,
    setValue,
    getValues,
    clearErrors,
    formState: { errors },
  } = useForm({
    mode: 'onBlur',
    defaultValues: initialValues,
    resolver: yupResolver(CampaignEditSchema),
  })

  const onSubmit = (data) => {
    Inertia.put(route('admin.campaigns.update', { id: data.id }), data, {})
  }
  const onError = () => {}

  return (
    <GridCustomizer container component="form" spacing={2} onSubmit={handleSubmit(onSubmit, onError)}>
      <Grid item xs={12}>
        <Section title={t('CAMPAIGN.BASIC_INFORMATION')}>
          <Grid container>
            <Grid item lg={7} xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <LabelField required title={t('CAMPAIGN.NAME')}>
                    <TextField
                      fullWidth
                      name="name"
                      size="small"
                      variant="outlined"
                      {...register('name')}
                      error={errors.name ? true : false}
                      helperText={errors.name?.message}
                      onFocus={() => trigger('name')}
                    />
                  </LabelField>
                </Grid>
                <Grid item xs={12}>
                  <LabelField title={t('CAMPAIGN.DESCRIPTION')}>
                    <DivCustomizer>
                      <CKEditor
                        config={{
                          removePlugins: ['EasyImage', 'ImageUpload', 'MediaEmbed'],
                        }}
                        data={getValues('description')}
                        editor={ClassicEditor}
                        id="description"
                        name="description"
                        onChange={(value, editor) => setValue('description', editor.getData())}
                      />
                    </DivCustomizer>
                  </LabelField>
                </Grid>
                <Grid item xs={12}>
                  <LabelField required title={t('CAMPAIGN.STATUS')}>
                    <Controller
                      control={control}
                      name="status"
                      render={() => (
                        <TextField
                          fullWidth
                          select
                          name="status"
                          size="small"
                          value={getValues('status')}
                          variant="outlined"
                          {...register('status')}
                          error={errors.status ? true : false}
                          helperText={errors.status?.message}
                        >
                          {status.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {t(option.name)}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                  </LabelField>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Section>
      </Grid>
      <Grid item xs={12}>
        <Section title={t('CAMPAIGN.PARTNER')}>
          <Grid container>
            <Grid item lg={7} xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <LabelField required title={t('CAMPAIGN.CHOOSE_PARTNER')}>
                    <Controller
                      control={control}
                      name="partner_id"
                      render={() => (
                        <Autocomplete
                          disabled
                          fullWidth
                          getOptionLabel={(option) => option.name}
                          id="tags-outlined"
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          options={partners}
                          renderInput={(params) => (
                            <TextField
                              fullWidth
                              {...params}
                              error={errors.partner_id ? true : false}
                              helperText={errors.partner_id?.message}
                              {...register('partner_id')}
                              id="partner_id"
                              variant="outlined"
                            />
                          )}
                          size="small"
                          value={getValues('partner')}
                          onChange={(e, value) => setValue('partner_id', value?.id || '')}
                        />
                      )}
                    />
                  </LabelField>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Section>
      </Grid>
      <Grid item xs={12}>
        <Section title={t('CAMPAIGN.LINE_OF_CREDIT')}>
          <Grid container>
            <Grid item lg={7} xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <LabelField
                    required
                    recommend={t('CAMPAIGN.LINE_OF_CREDIT_RECOMMEND')}
                    title={t('CAMPAIGN.LINE_OF_CREDIT')}
                  >
                    <TextField
                      fullWidth
                      name="line_of_credit"
                      size="small"
                      variant="outlined"
                      {...register('line_of_credit')}
                      error={errors.line_of_credit ? true : false}
                      helperText={errors.line_of_credit?.message}
                      onFocus={() => trigger('line_of_credit')}
                    />
                  </LabelField>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Section>
      </Grid>
      <Grid item xs={12}>
        <Section title={t('CAMPAIGN.LIMIT')}>
          <Grid container>
            <Grid item lg={7} xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <LabelField required title={t('CAMPAIGN.LIMIT_CODE_USER_PER_DAY')}>
                    <TextField
                      fullWidth
                      name="codes_per_day"
                      size="small"
                      type="number"
                      variant="outlined"
                      {...register('codes_per_day')}
                      error={errors.codes_per_day ? true : false}
                      helperText={errors.codes_per_day?.message}
                      onFocus={() => trigger('codes_per_day')}
                    />
                  </LabelField>
                </Grid>
                <Grid item xs={12}>
                  <LabelField required title={t('CAMPAIGN.TIME_BETWEEN_REDEEM_CODE')}>
                    <TextField
                      {...register('time_between_redeem')}
                      fullWidth
                      error={errors.time_between_redeem ? true : false}
                      helperText={errors.time_between_redeem?.message}
                      name="time_between_redeem"
                      size="small"
                      type="number"
                      variant="outlined"
                    />
                  </LabelField>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Section>
      </Grid>
      <Grid item xs={12}>
        <Section title={t('CAMPAIGN.TIME')}>
          <Grid container>
            <Grid item lg={7} xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <LabelField required title={t('CAMPAIGN.START_TIME')}>
                      <DateTimePicker
                        inputFormat={LOCALISE.DATE_TIME_FORMAT}
                        minDateTime={new Date()}
                        name="visible_from"
                        renderInput={(params) => <TextField fullWidth {...params} />}
                        {...register('start_at')}
                        value={getValues('start_at')}
                        onChange={(value) => {
                          setValue('start_at', value, { shouldValidate: true, shouldDirty: true })
                        }}
                      />
                      <FormHelperText error={!!errors.start_at} sx={{ pl: 2 }}>
                        {errors.start_at?.message}
                      </FormHelperText>
                    </LabelField>
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <LabelField required title={t('CAMPAIGN.END_TIME')}>
                      <DateTimePicker
                        inputFormat={LOCALISE.DATE_TIME_FORMAT}
                        minDateTime={new Date()}
                        name="end_at"
                        renderInput={(params) => <TextField fullWidth {...params} />}
                        {...register('end_at')}
                        value={getValues('end_at')}
                        onChange={(value) => {
                          setValue('end_at', value, { shouldValidate: true, shouldDirty: true })
                        }}
                      />
                      <FormHelperText error={!!errors.end_at} sx={{ pl: 2 }}>
                        {errors.end_at?.message}
                      </FormHelperText>
                    </LabelField>
                  </LocalizationProvider>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Section>
      </Grid>
      <Grid item xs={12}>
        <Section title={t('CAMPAIGN.TIME_DISPLAY')}>
          <Grid container>
            <Grid item lg={7} xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <LabelField required title={t('CAMPAIGN.START_DISPLAY')}>
                      <DateTimePicker
                        inputFormat={LOCALISE.DATE_TIME_FORMAT}
                        minDateTime={new Date()}
                        name="visible_from"
                        renderInput={(params) => <TextField fullWidth {...params} />}
                        {...register('visible_from')}
                        value={getValues('visible_from')}
                        onChange={(value) => {
                          setValue('visible_from', value, { shouldValidate: true, shouldDirty: true })
                        }}
                      />
                      <FormHelperText error={!!errors.visible_from} sx={{ pl: 2 }}>
                        {errors.visible_from?.message}
                      </FormHelperText>
                    </LabelField>
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <LabelField required title={t('CAMPAIGN.END_DISPLAY')}>
                      <DateTimePicker
                        inputFormat={LOCALISE.DATE_TIME_FORMAT}
                        minDateTime={new Date()}
                        name="visible_to"
                        renderInput={(params) => <TextField fullWidth {...params} />}
                        {...register('visible_to')}
                        value={getValues('visible_to')}
                        onChange={(value) => {
                          setValue('visible_to', value, { shouldValidate: true, shouldDirty: true })
                        }}
                      />
                      <FormHelperText error={!!errors.visible_to} sx={{ pl: 2 }}>
                        {errors.visible_to?.message}
                      </FormHelperText>
                    </LabelField>
                  </LocalizationProvider>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Section>
      </Grid>

      <Grid item xs={12}>
        <Section title={t('CAMPAIGN.IMAGE')}>
          <Grid container>
            <Grid item lg={7} xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <LabelField required recommend={t('CAMPAIGN.SLIDER_RECOMMEND')} title={t('CAMPAIGN.SLIDER')}>
                    <Controller
                      control={control}
                      name="images"
                      render={({ field }) => (
                        <InputUpload
                          multiple
                          {...field}
                          buttonName={t('CAMPAIGN.UPLOAD_SLIDER')}
                          clearErrors={clearErrors}
                          handleImageChange={(files) => setValue('images', files)}
                          id="images"
                          name="images"
                          ref={field.ref}
                          title={t('CAMPAIGN.UPLOAD_SLIDER')}
                          value={getValues('images')}
                        />
                      )}
                    />

                    {errors.images && (
                      <TypographyCustomizer variant="caption">{errors.images?.message}</TypographyCustomizer>
                    )}
                  </LabelField>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Section>
      </Grid>
      <Grid item xs="auto">
        <Button color="primary" type="submit" variant="contained">
          {t('BUTTON.SAVE')}
        </Button>
      </Grid>

      <Grid item xs="auto">
        <Button color="inherit" component={InertiaLink} href={route('admin.campaigns.index')} variant="contained">
          {t('BUTTON.CANCEL')}
        </Button>
      </Grid>
    </GridCustomizer>
  )
}

export default CampaignEdit
